import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Filter, Phone, AlertCircle, Mail } from 'lucide-react';
import AnimalCard from '../components/AnimalCard';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [animals, setAnimals] = useState([]);
  const [filteredAnimals, setFilteredAnimals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpecies, setFilterSpecies] = useState('All');
  const [filterVaccinated, setFilterVaccinated] = useState('All');
  const [filterSterilized, setFilterSterilized] = useState('All');

  useEffect(() => {
    fetchAnimals();
    fetchContacts();
    fetchIncidents();
  }, []);

  useEffect(() => {
    filterAnimals();
  }, [animals, searchTerm, filterSpecies, filterVaccinated, filterSterilized]);

  const fetchAnimals = async () => {
    try {
      const response = await axios.get(`${API}/animals`);
      setAnimals(response.data.filter(a => a.approved));
    } catch (error) {
      console.error('Error fetching animals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data.filter(c => c.available).slice(0, 4));
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchIncidents = async () => {
    try {
      const response = await axios.get(`${API}/incidents`);
      setIncidents(response.data);
    } catch (error) {
      console.error('Error fetching incidents:', error);
    }
  };

  const filterAnimals = () => {
    let filtered = animals;

    if (searchTerm) {
      filtered = filtered.filter(
        (animal) =>
          animal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          animal.area.toLowerCase().includes(searchTerm.toLowerCase()) ||
          animal.colour.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterSpecies !== 'All') {
      filtered = filtered.filter((animal) => animal.species === filterSpecies);
    }

    if (filterVaccinated !== 'All') {
      filtered = filtered.filter(
        (animal) => animal.vaccinated === (filterVaccinated === 'Yes')
      );
    }

    if (filterSterilized !== 'All') {
      filtered = filtered.filter(
        (animal) => animal.sterilized === (filterSterilized === 'Yes')
      );
    }

    setFilteredAnimals(filtered);
  };

  const stats = {
    total: animals.length,
    dogs: animals.filter((a) => a.species === 'Dog').length,
    cats: animals.filter((a) => a.species === 'Cat').length,
    vaccinated: animals.filter((a) => a.vaccinated).length,
    sterilized: animals.filter((a) => a.sterilized).length,
  };

  const incidentStats = {
    total: incidents.length,
    open: incidents.filter(i => i.status === 'Open').length,
    resolved: incidents.filter(i => i.status === 'Resolved').length,
  };

  return (
    <div className="min-h-screen">
      <div className="bg-gradient-to-br from-amber-400 to-orange-500 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 font-heading" data-testid="dashboard-title">
              Stray Animal Records
            </h1>
            <p className="text-lg text-white/90 font-medium" data-testid="dashboard-subtitle">
              Managing stray animals in Mumbai with care and compassion
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white p-6 rounded-2xl border border-orange-50 shadow-sm" data-testid="stat-total">
            <div className="text-3xl font-extrabold text-primary mb-1">{stats.total}</div>
            <div className="text-sm font-bold text-text-secondary">Total Animals</div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-orange-50 shadow-sm" data-testid="stat-dogs">
            <div className="text-3xl font-extrabold text-primary mb-1">{stats.dogs}</div>
            <div className="text-sm font-bold text-text-secondary">Dogs</div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-orange-50 shadow-sm" data-testid="stat-cats">
            <div className="text-3xl font-extrabold text-primary mb-1">{stats.cats}</div>
            <div className="text-sm font-bold text-text-secondary">Cats</div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-orange-50 shadow-sm" data-testid="stat-vaccinated">
            <div className="text-3xl font-extrabold text-green-600 mb-1">{stats.vaccinated}</div>
            <div className="text-sm font-bold text-text-secondary">Vaccinated</div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-orange-50 shadow-sm" data-testid="stat-sterilized">
            <div className="text-3xl font-extrabold text-violet-600 mb-1">{stats.sterilized}</div>
            <div className="text-sm font-bold text-text-secondary">Sterilized</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-2xl border border-orange-100 shadow-sm hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate('/incidents')} data-testid="incidents-summary">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-red-50 p-3 rounded-xl">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-text-primary font-heading">Incidents</h3>
              </div>
              <span className="text-xs font-bold text-primary hover:underline">View All →</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">Total</span>
                <span className="text-2xl font-extrabold text-primary">{incidentStats.total}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">Open</span>
                <span className="text-xl font-bold text-red-600">{incidentStats.open}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">Resolved</span>
                <span className="text-xl font-bold text-green-600">{incidentStats.resolved}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-orange-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-50 p-3 rounded-xl">
                  <Phone className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-text-primary font-heading">Emergency Contacts</h3>
              </div>
              <button onClick={() => navigate('/emergency')} className="text-xs font-bold text-primary hover:underline">
                View All →
              </button>
            </div>
            {contacts.length === 0 ? (
              <p className="text-sm text-text-muted text-center py-4">No contacts added yet</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {contacts.map(contact => (
                  <a
                    key={contact.id}
                    href={`tel:${contact.phone}`}
                    data-testid={`contact-quick-${contact.id}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-br from-blue-50 to-sky-50 hover:from-blue-100 hover:to-sky-100 transition-all group border border-blue-100"
                  >
                    <div className="bg-blue-600 p-2 rounded-lg">
                      <Phone className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-blue-700 mb-0.5 truncate">{contact.role}</div>
                      <div className="text-sm font-bold text-text-primary truncate">{contact.name}</div>
                      <div className="text-xs text-text-muted">{contact.phone}</div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-orange-100 shadow-sm mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold text-text-primary font-heading">Search & Filter</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input
                type="text"
                placeholder="Search by name, area, color..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
                className="w-full h-12 pl-12 pr-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
              />
            </div>

            <select
              value={filterSpecies}
              onChange={(e) => setFilterSpecies(e.target.value)}
              data-testid="filter-species"
              className="h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
            >
              <option value="All">All Species</option>
              <option value="Dog">Dogs</option>
              <option value="Cat">Cats</option>
            </select>

            <select
              value={filterVaccinated}
              onChange={(e) => setFilterVaccinated(e.target.value)}
              data-testid="filter-vaccinated"
              className="h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
            >
              <option value="All">Vaccination Status</option>
              <option value="Yes">Vaccinated</option>
              <option value="No">Not Vaccinated</option>
            </select>

            <select
              value={filterSterilized}
              onChange={(e) => setFilterSterilized(e.target.value)}
              data-testid="filter-sterilized"
              className="h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
            >
              <option value="All">Sterilization Status</option>
              <option value="Yes">Sterilized</option>
              <option value="No">Not Sterilized</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-indicator">
            <div className="text-xl text-text-secondary font-medium">Loading animals...</div>
          </div>
        ) : filteredAnimals.length === 0 ? (
          <div className="text-center py-12" data-testid="no-animals">
            <div className="text-xl text-text-secondary font-medium mb-2">No animals found</div>
            <p className="text-text-muted">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-8">
            {filteredAnimals.map((animal) => (
              <AnimalCard key={animal.id} animal={animal} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;